/**
 * Whether this browser can install the app, how, and whether now is the moment
 * to say so.
 *
 * Two questions that look like one. CAN it be installed is a fact about the
 * browser, and the answer is never a plain yes or no: Chromium hands the page a
 * `beforeinstallprompt` event it can fire itself, and iOS Safari has no such
 * event and installs only through the share sheet. A card whose button says
 * "Install" is a lie on one of those, which is why the support state carries the
 * instruction with it.
 *
 * SHOULD we say so is a judgement, and it is the one that keeps the offer
 * non-disturbing: an app that asks to be installed before it has been used is
 * asking a stranger to move in. Pure — the browser facts arrive as arguments, so
 * every branch is reachable from a plain Node process.
 */

/**
 * How this browser installs, most actionable first.
 *
 * Two paths and no more, which is a narrower set than the sibling product's, and
 * deliberately: the message catalogue carries an instruction for the iOS share
 * sheet and one for a Chromium menu, and nothing else. Desktop Safari's Add to
 * Dock and Firefox for Android's menu item would each need a sentence written
 * for them, and an offer that names the wrong menu is worse than no offer — so
 * they fall through to `unsupported` and this app says nothing on them. That is
 * a content decision the day someone writes the copy, not a code change.
 */
export const INSTALL_SUPPORT = {
    installed: 'installed',
    nativePrompt: 'native-prompt',
    iosShare: 'ios-share',
    chromiumMenu: 'chromium-menu',
    unsupported: 'unsupported',
};

/**
 * The instruction to print under the offer, per support state.
 *
 * Only `native-prompt` has a button that installs anything; the other state
 * needs the sentence, because the primary action could do no more than repeat
 * it.
 */
const HOW_TO_KEYS = {
    [INSTALL_SUPPORT.iosShare]: 'pwa.install.how.iosShare',
    [INSTALL_SUPPORT.chromiumMenu]: 'pwa.install.how.chromiumMenu',
};

/** A dismissed offer is not a refused one, but it is not a question to re-ask soon. */
export const INSTALL_DISMISSAL_HOLD_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Not on the first visit.
 *
 * The install offer is a claim that the app is worth keeping, and on visit one
 * the visitor has no way to judge that. Two is the smallest number that means
 * "came back".
 */
export const VISITS_BEFORE_INSTALL_OFFER = 2;

export function readInstallSupport({
    userAgent = '',
    platform = '',
    maxTouchPoints = 0,
    isStandalone = false,
    hasDeferredPrompt = false,
} = {}) {
    if (isStandalone) {
        return INSTALL_SUPPORT.installed;
    }

    // iPadOS reports itself as a Mac and is separated from a real one only by
    // the touch points — without this an iPad is told to open a menu it does not
    // have.
    const isIOS = /iPad|iPhone|iPod/u.test(userAgent) || (platform === 'MacIntel' && Number(maxTouchPoints) > 1);
    const isAndroid = /Android/iu.test(userAgent);
    const isChromiumFamily = /Chrome|Chromium|Edg|OPR|SamsungBrowser/iu.test(userAgent);
    const isFirefoxFamily = /Firefox|FxiOS/iu.test(userAgent);

    // The event outranks every guess below it: a browser that handed us a prompt
    // can install, whatever its user agent claims to be.
    if (hasDeferredPrompt) {
        return INSTALL_SUPPORT.nativePrompt;
    }

    // EVERY browser on iOS, not only Safari. iOS has exactly one door regardless
    // of badge, and since 16.4 every browser on it can put an app on the home
    // screen through the share sheet.
    if (isIOS) {
        return INSTALL_SUPPORT.iosShare;
    }

    // Firefox has no install path this app has copy for, on either platform, and
    // it must not be swept into the Chromium branch below — every Firefox user
    // agent on Android also matches "Android".
    if (isFirefoxFamily) {
        return INSTALL_SUPPORT.unsupported;
    }

    return isChromiumFamily || isAndroid ? INSTALL_SUPPORT.chromiumMenu : INSTALL_SUPPORT.unsupported;
}

/** An installed app has nothing to offer; an unknown browser has nothing to say. */
export function canOfferInstall(support) {
    return support !== INSTALL_SUPPORT.installed && support !== INSTALL_SUPPORT.unsupported;
}

export function installHowToKey(support) {
    return HOW_TO_KEYS[support] || null;
}

/**
 * The label for a button that installs — and `null` where there is no such
 * button to draw.
 *
 * `native-prompt` is the only state the page can act on. Everywhere else the
 * instruction under the heading IS the action, and a button beside it could only
 * repeat the sentence while looking like it did something. A control that does
 * nothing is worse than no control, so there is no control.
 */
export function installActionKey(support) {
    return support === INSTALL_SUPPORT.nativePrompt ? 'pwa.install.action' : null;
}

/**
 * Is now the moment?
 *
 * Returns the reason it is not, rather than a bare false, so the hook can be
 * debugged from its return value instead of from a breakpoint.
 */
export function resolveInstallOffer({
    support = INSTALL_SUPPORT.unsupported,
    visitCount = 0,
    dismissedAt = null,
    installedAt = null,
    now = 0,
    holdMs = INSTALL_DISMISSAL_HOLD_MS,
} = {}) {
    if (!canOfferInstall(support)) {
        return { canOffer: false, reason: support === INSTALL_SUPPORT.installed ? 'installed' : 'unsupported' };
    }

    // Standalone is the only signal that says "installed", and the tab that DID
    // the installing never has it — so without this the card returns to the one
    // person who provably does not need it, carrying the browser-menu
    // instruction, on the next navigation back to a tabbed screen.
    if (Number.isFinite(installedAt) && installedAt > 0) {
        return { canOffer: false, reason: 'installed' };
    }

    if (visitCount < VISITS_BEFORE_INSTALL_OFFER) {
        return { canOffer: false, reason: 'too-early' };
    }

    if (Number.isFinite(dismissedAt) && dismissedAt > 0 && now - dismissedAt < holdMs) {
        return { canOffer: false, reason: 'dismissed' };
    }

    return { canOffer: true, reason: null };
}
